import React, {useContext, useEffect, useState} from "react";

import * as ApiCalls from "./ApiCalls.js";
import Loading from "./Loading";
import LinkToMainPage from "./LinkToMainPage";

import {azureTokenContext, bookVisibilityContext, userIdContext} from "../../App";

import moment from "moment";
import "moment/locale/nb";

import {TextField, IconButton, InputAdornment} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import "../../style/PriorityTableStyles/taskstable.css";
import SearchIcon from "@mui/icons-material/Search";
import {PaginationTaskTable} from "./PriorityTableComponents/PaginationTaskTable";
import SelectTargetUser from "./PriorityTableComponents/SelectTargetUser";
import BookVisibilityToggle from "./BookVisibilityToggle";
import {targetUserIdContext} from "../MainPart";
import OpenDocumentDropdown from "./PriorityTableComponents/OpenDocumentDropdown";
import {useLogMetric} from "../../hooks/useLogMetric";

interface PublishedTaskType {
    DocumentTitle: string;
    Source: string;
    DocumentId: string;
    Status: string;
    Version: string;
    Created: string;
    Id: string;
    PublishedDateChangedDate: string | null;
    SharepointFolder: string;
    SharepointPath: string;
}

export function PublishedTasksTable() {
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const [targetUserId] = useContext(targetUserIdContext);
    const [includeBooks] = useContext(bookVisibilityContext);
    const logMetric = useLogMetric();

    const [allPublishedTasks, setAllPublishedTasks] = useState<PublishedTaskType[]>([]);
    const [selectedTasks, setSelectedTasks] = useState<PublishedTaskType[]>([]);


    const visibleResultNumber = 10;
    const [pageCounter, setPageCounter] = useState<number>(0);
    const [totalPageNumber, setTotalPageNumber] = useState<number>(1);

    const [loadingStatus, setLoadingStatus] = useState("loading");

    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        setLoadingStatus("loading");
        ApiCalls.getPublishedTasks(targetUserId, azureToken, includeBooks)
            .then((res) => {
                const convertedTasks = convertPublishedTasks(res.Result);

                setAllPublishedTasks(convertedTasks);
                setSelectedTasks(convertedTasks);

                setTotalPageNumber(Math.ceil(convertedTasks.length / visibleResultNumber));
                setPageCounter(1);

                setLoadingStatus("done");
                ApiCalls.log("INFO", "Opening published tasks table successful.", "AuthorsPortal", "", "", userId, azureToken);
            })
            .catch((error) => {
                setLoadingStatus("error");
                const errorMessage = error instanceof Error ? error.message : String(error);
                ApiCalls.log("ERROR", `Failed getPublishedTasks: ${errorMessage}`, "AuthorsPortal", "", "", userId, azureToken);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, targetUserId, includeBooks]);

    const resetSearchState = () => {
        setSearchValue("");
        setSelectedTasks(allPublishedTasks);
        setTotalPageNumber(Math.ceil(allPublishedTasks.length / visibleResultNumber));
        setPageCounter(1);
    };

    useEffect(() => {
        resetSearchState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetUserId, userId]);

    const handleSearchClear = () => {
        resetSearchState();
        logMetric({category: "PublishedTasks", subCategory: "Search cleared", message: "User cleared search in published tasks"});
    };

    const handleSearchInputChange = (event) => {
        setSearchValue(event.target.value);
        const filteredTasks = allPublishedTasks.filter((item) =>
            Object.values(item).some((value) => String(value).toLowerCase().includes(event.target.value.toLowerCase()))
        );
        setSelectedTasks(filteredTasks);
        setTotalPageNumber(Math.ceil(filteredTasks.length / visibleResultNumber));
        setPageCounter(1);
    };

    return (
        <>
            <>{loadingStatus === "loading" && <Loading/>}</>
            <>
                {loadingStatus === "done" && (
                    <>
                        <LinkToMainPage/>

                        <div className="tasks-table-container published-tasks-table-container">

                            <SelectTargetUser/>
                            <BookVisibilityToggle/>

                            <div className="table-header-row">
                                <p className="tasks-table-title">Publiserte oppgaver</p>
                                <TextField
                                    className="search-box"
                                    placeholder="Søk i oppgaver"
                                    variant="standard"
                                    value={searchValue}
                                    onChange={handleSearchInputChange}
                                    sx={{display: "inline-block", marginBottom: "15px"}}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon/>
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {searchValue && (
                                                    <IconButton onClick={handleSearchClear} edge="end">
                                                        <ClearIcon/>
                                                    </IconButton>
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                            <table className="tasks-table">
                                <thead>
                                <tr>
                                    <th className="table-head-task-name">Publikasjon</th>
                                    <th className="table-head-link"></th>
                                    <th className="table-head-duedate">Publisert</th>
                                    <th className="table-head-version">Versjon</th>
                                </tr>
                                </thead>
                                {selectedTasks && selectedTasks.length === 0 && (
                                    <tbody>
                                    <tr>
                                        <td colSpan={7}>
                                            <p className="no-results-message">{searchValue ? "Ingen resultater" : "Ingen oppgaver"}</p>
                                        </td>
                                    </tr>
                                    </tbody>
                                )}
                                {selectedTasks && selectedTasks.length > 0 && (
                                    <tbody>
                                    {selectedTasks
                                        .slice((pageCounter - 1) * visibleResultNumber, pageCounter * visibleResultNumber)
                                        .map((row: PublishedTaskType) => (
                                            <tr key={row.Id}>
                                                <td className="table-body-task-name">{row.DocumentTitle}</td>
                                                <td className="table-body-link">
                                                    {row.SharepointFolder && row.SharepointPath !== "" && (
                                                        <div className="open-document-dropdown-container">
                                                            <div className="open-document-div">
                                                                <div className="open-document-link">
                                                                    <OpenDocumentDropdown row={row}/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="table-body-duedate">{row.PublishedDateChangedDate}</td>
                                                <td>{row.Version}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                )}
                            </table>
                            <div className="table-footer-row">
                                <div className="pagination-container">
                                    <PaginationTaskTable pageCounter={pageCounter} totalPageNumber={totalPageNumber}
                                                         setPageCounter={setPageCounter}/>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </>
            <>{loadingStatus === "error" && "error"}</>
        </>
    );
}

export function convertPublishedTasks(tasks: any) {
    return tasks.map((item) => {
        return {
            ...item,
            PublishedDateChangedDate: item.PublishedDate.ChangedDate
                ? moment(item.PublishedDate.ChangedDate).format("ll")
                : item.PublishedDate,
        };
    });
}
